import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check } from 'lucide-react';
import { currencyMeta, format, formatHuman, parse } from '@fint/money';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useBaseCurrency, useToday } from '@/hooks/useOrgSettings';
import { formatDateLong } from '@/lib/dates';
import { Button, CategoryMark, ErrorState, Input, Spinner } from '@/features/common/ui';
import { confirmationSentence, dayBefore, normaliseAmount, type QuickAddKind } from './quick-add';

/**
 * Quick add: the screen a ten-year-old can use.
 *
 * ── WHY A SECOND WAY TO RECORD AN EXPENSE ──
 * The composer on /expenses has tax, billing, a customer, a project, tags and
 * a receipt. All of it earns its place for an accountant, and all of it is
 * why the owner of a small business photographs the receipt and files it
 * "later". The owner's words: "even a 10 year old can update his income and
 * expense". So this asks four things, one per screen, in the order a person
 * would say them - what happened, what for, how much, is that right - and
 * writes the SAME records the composer does. Nothing here is a draft or a
 * lighter kind of expense; it is the ordinary expense with the fields a
 * receipt does not need left at their defaults (tax included, not billable,
 * no project).
 *
 * ── ONE THING PER SCREEN, AND A SENTENCE AT THE END ──
 * Every step fits on a phone without scrolling and has one decision on it.
 * The confirmation is a sentence rather than a summary table, because a
 * sentence can be read by someone who does not know what "paid through" is,
 * and read WRONG only if the record is wrong - which is the check.
 *
 * ── PERMISSIONS ──
 * Spending needs `expense.create`; money in is a bank deposit and needs
 * `banking.create`, and naming what kind of income it was needs
 * `banking.manage`. A tile the person cannot use is shown disabled with the
 * reason, not hidden: a screen that offers one big button reads as broken.
 * The server checks all of it again; this only stops a tap that would 403.
 */

interface CategoryTile {
  id: string;
  name: string;
  colour: string;
  emoji: string | null;
}

interface Account {
  id: string;
  name: string;
  currency: string;
  isPrimary: boolean;
  isInvestment?: boolean;
}

type Step = 'kind' | 'category' | 'amount' | 'confirm' | 'saved';

export function QuickAddPage() {
  const org = useActiveOrg();
  const orgId = org?.id;
  const perms = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const today = useToday();
  const baseCurrency = useBaseCurrency();

  /**
   * ── THE HOME-SCREEN SHORTCUTS ──
   * The installed app's icon offers "Add expense" and "Add income", which
   * open /add?kind=spend or ?kind=income. Landing on step 1 and asking
   * "what happened?" would make the shortcut a tap slower than no shortcut,
   * so a named kind skips straight to the category tiles - and is dropped
   * from the URL so a Back does not re-skip.
   */
  const [params, setParams] = useSearchParams();
  const shortcut: QuickAddKind | null =
    params.get('kind') === 'spend' ? 'spent' : params.get('kind') === 'income' ? 'got' : null;
  const [step, setStep] = useState<Step>(shortcut ? 'category' : 'kind');
  const [kind, setKind] = useState<QuickAddKind>(shortcut ?? 'spent');
  useEffect(() => {
    if (shortcut) setParams({}, { replace: true });
  }, [shortcut, setParams]);
  const [category, setCategory] = useState<CategoryTile | null>(null);
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<string>(today);
  const [accountId, setAccountId] = useState<string>('');
  const [note, setNote] = useState('');

  const canSpend = perms.can('expense', 'create');
  const canReceive = perms.can('banking', 'create');
  const canClassifyIncome = perms.can('banking', 'manage');

  const expenseCategories = useQuery({
    queryKey: ['expense-categories', orgId],
    queryFn: () => api.get<{ items: CategoryTile[] }>('/expenses/categories'),
    enabled: !!orgId && kind === 'spent' && step !== 'kind',
  });
  const incomeCategories = useQuery({
    queryKey: ['income-categories', orgId],
    queryFn: () => api.get<{ items: CategoryTile[] }>('/income-categories'),
    enabled: !!orgId && kind === 'got' && step !== 'kind' && canClassifyIncome,
  });
  const accounts = useQuery({
    queryKey: ['banking', orgId, 'accounts'],
    queryFn: () => api.get<{ items: Account[] }>('/banking/accounts'),
    enabled: !!orgId && step !== 'kind',
  });

  // The primary account, or the first one, the moment the list arrives - so
  // the amount screen never opens with "from nowhere". Chosen once; a person
  // who changed it is not overridden by a refetch.
  // Never an FD, fund or chit: money is not spent out of, or received into, a
  // deposit from here. Moving money into one is a transfer, said on Banking.
  const accountList = useMemo(() => (accounts.data?.items ?? []).filter((a) => !a.isInvestment), [accounts.data]);
  useEffect(() => {
    if (accountId || accountList.length === 0) return;
    setAccountId((accountList.find((a) => a.isPrimary) ?? accountList[0]).id);
  }, [accountId, accountList]);
  const account = accountList.find((a) => a.id === accountId) ?? null;
  const currency = account?.currency ?? baseCurrency;

  const normalisedAmount = normaliseAmount(amount);
  const amountText = normalisedAmount ? formatHuman(parse(normalisedAmount, currency)) : '';

  const save = useMutation({
    mutationFn: async () => {
      if (!normalisedAmount || !account) throw new Error('Nothing to save yet');
      // Through @fint/money so the wire carries "450.00", never "450." or
      // "1,250" - the same shape every other writer sends.
      const wire = format(parse(normalisedAmount, currency));
      if (kind === 'spent') {
        if (!category) throw new Error('Pick a category first');
        await api.post('/expenses', {
          date,
          categoryId: category.id,
          amount: wire,
          paidThroughAccountId: account.id,
          // What the receipt says, tax included, no rate. The composer exists
          // for the person who needs to split the GST out.
          taxInclusive: true,
          notes: note.trim() || undefined,
        });
        return;
      }
      const row = await api.post<{ id: string }>('/banking/transactions', {
        accountId: account.id,
        date,
        direction: 'deposit',
        amount: wire,
        // The description is what the Banking list shows; without one the
        // row reads as a dash. The category's name is the honest default.
        description: note.trim() || (category ? category.name : 'Money in'),
      });
      if (category) {
        await api.put(`/banking/transactions/${row.id}/income-category`, { incomeCategoryId: category.id });
      }
    },
    onSuccess: () => {
      // Every screen that shows this money, so it is there when they look.
      void queryClient.invalidateQueries({ queryKey: ['expenses', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['banking', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-breakdown', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-overview', orgId] });
      setStep('saved');
    },
  });

  const startOver = () => {
    setStep('kind');
    setCategory(null);
    setAmount('');
    setDate(today);
    setNote('');
    save.reset();
  };

  const back = () => {
    if (step === 'category') setStep('kind');
    else if (step === 'amount') setStep('category');
    else if (step === 'confirm') setStep('amount');
  };

  const categories = kind === 'spent' ? expenseCategories : incomeCategories;
  const stepNumber = { kind: 1, category: 2, amount: 3, confirm: 4, saved: 4 }[step];

  return (
    /*
      ── THE WHOLE HEIGHT, AND A FOOTER THAT STAYS ──
      `min-h-full` of the scroller, as a column, so a step's primary button
      can sit in a sticky footer at the bottom of the SCREEN rather than at
      the bottom of the step's content. On an iPhone SE the amount step's Next
      was the first thing under the fold - and with the keyboard up, which is
      how that step opens, it was two screens down. The shell drops its own
      header and tab bar on this route on a phone (see DashboardLayout) for
      the same reason: 568 points minus a 300-point keyboard leaves 268, and
      a book name, a step header, the amount box and Next are 253 of them.
      `pt-safe-top` because that shell header was what used to clear the
      notch here.
    */
    <div className="mx-auto flex min-h-full w-full max-w-lg flex-col px-4 pt-[max(1rem,var(--safe-top))] sm:px-5 sm:pt-6">
      {/* One header for every step: where you are, and the way back. */}
      <div className="mb-4 flex items-center gap-2">
        {step !== 'kind' && step !== 'saved' ? (
          <Button variant="ghost" size="icon" onClick={back} aria-label="Back">
            <ArrowLeft className="size-5" />
          </Button>
        ) : (
          <Button variant="ghost" size="icon" asChild aria-label="Close and go to the dashboard">
            <Link to="/">
              <ArrowLeft className="size-5" />
            </Link>
          </Button>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-lg font-semibold tracking-tight">Add</h1>
          <p className="text-xs text-muted-foreground" aria-live="polite">
            Step {stepNumber} of 4
          </p>
        </div>
      </div>

      {step === 'kind' ? (
        <KindStep
          canSpend={canSpend}
          canReceive={canReceive}
          onPick={(picked) => {
            setKind(picked);
            setCategory(null);
            setStep('category');
          }}
        />
      ) : null}

      {step === 'category' ? (
        <CategoryStep
          kind={kind}
          tiles={categories.data?.items ?? []}
          loading={categories.isPending && categories.fetchStatus !== 'idle'}
          error={categories.isError ? categories.error : undefined}
          onRetry={() => {
            void categories.refetch();
          }}
          // Money in with no category is still money in: the deposit is
          // written and the dashboard reports it under "nobody has said what
          // it was". An expense has to have one, so only income gets the tile.
          allowNone={kind === 'got'}
          onPick={(tile) => {
            setCategory(tile);
            setStep('amount');
          }}
        />
      ) : null}

      {step === 'amount' ? (
        <AmountStep
          kind={kind}
          currency={currency}
          amount={amount}
          onAmount={setAmount}
          valid={!!normalisedAmount}
          date={date}
          today={today}
          onDate={setDate}
          accounts={accountList}
          accountsLoading={accounts.isPending}
          accountsError={accounts.isError ? accounts.error : undefined}
          accountId={accountId}
          onAccount={setAccountId}
          note={note}
          onNote={setNote}
          onNext={() => setStep('confirm')}
        />
      ) : null}

      {step === 'confirm' && account ? (
        <ConfirmStep
          sentence={confirmationSentence({
            kind,
            amountText,
            categoryName: category?.name ?? 'nothing in particular',
            categoryEmoji: category?.emoji ?? null,
            accountName: account.name,
            date,
            today,
          })}
          note={note.trim()}
          pending={save.isPending}
          error={save.error instanceof ApiError ? save.error.message : save.error ? String(save.error) : undefined}
          onSave={() => save.mutate()}
          onChange={() => setStep('amount')}
        />
      ) : null}

      {step === 'saved' ? (
        <SavedStep
          sentence={confirmationSentence({
            kind,
            amountText,
            categoryName: category?.name ?? 'nothing in particular',
            categoryEmoji: category?.emoji ?? null,
            accountName: account?.name ?? '',
            date,
            today,
          })}
          onAnother={startOver}
          onDone={() => navigate(kind === 'spent' ? '/expenses' : '/banking')}
        />
      ) : null}
    </div>
  );
}

// ── Step 1: what happened ─────────────────────────────────────────────────

function KindStep({
  canSpend,
  canReceive,
  onPick,
}: {
  canSpend: boolean;
  canReceive: boolean;
  onPick: (kind: QuickAddKind) => void;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3">
      <p className="text-base font-medium">What happened?</p>
      <BigTile
        emoji="💸"
        title="I spent money"
        caption="A bill, a meal, fuel, anything you paid for."
        disabledReason={canSpend ? undefined : 'You are not allowed to record expenses in this book.'}
        onClick={() => onPick('spent')}
        testId="quick-add-spent"
      />
      <BigTile
        emoji="💰"
        title="I got money"
        caption="Cash or a transfer that came in."
        disabledReason={canReceive ? undefined : 'Only someone who can manage the bank accounts can record money in.'}
        onClick={() => onPick('got')}
        testId="quick-add-got"
      />
      <p className="mt-auto pb-[max(1rem,var(--safe-bottom))] pt-4 text-xs text-muted-foreground">
        Need tax, a customer or a receipt on it? Use{' '}
        <Link to="/expenses" className="underline" data-tap-exempt="inline text link in a sentence">
          the full expense form
        </Link>
        .
      </p>
    </div>
  );
}

/**
 * A tile the size of a thumb, twice over.
 *
 * Disabled is `aria-disabled` plus a reason underneath, not `disabled`: a
 * disabled button drops out of the tab order and out of a screen reader's
 * view, and the reason it is not offered is the one thing a person who
 * cannot use it needs to read.
 */
function BigTile({
  emoji,
  title,
  caption,
  disabledReason,
  onClick,
  testId,
}: {
  emoji: string;
  title: string;
  caption: string;
  disabledReason?: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-disabled={disabledReason ? true : undefined}
      aria-describedby={disabledReason ? `${testId}-why` : undefined}
      onClick={() => {
        if (!disabledReason) onClick();
      }}
      className={cn(
        'flex min-h-28 w-full items-center gap-4 rounded-xl border-2 border-input bg-card p-5 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        disabledReason ? 'cursor-not-allowed opacity-60' : 'hover:border-primary hover:bg-accent active:bg-accent',
      )}
    >
      <span aria-hidden="true" className="text-5xl leading-none">
        {emoji}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xl font-semibold">{title}</span>
        <span className="block text-sm text-muted-foreground">{caption}</span>
        {disabledReason ? (
          <span id={`${testId}-why`} className="mt-1 block text-xs text-destructive">
            {disabledReason}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// ── Step 2: what for ──────────────────────────────────────────────────────

function CategoryStep({
  kind,
  tiles,
  loading,
  error,
  onRetry,
  allowNone,
  onPick,
}: {
  kind: QuickAddKind;
  tiles: CategoryTile[];
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
  allowNone: boolean;
  onPick: (tile: CategoryTile | null) => void;
}) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" aria-busy="true">
        <Spinner className="size-6" />
        <span className="sr-only">Loading</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-base font-medium">{kind === 'spent' ? 'What was it for?' : 'What kind of money?'}</p>
      {/*
        A grid of tiles, not a list: the picture is the thing a person scans
        for, and pictures scan in two dimensions. The emoji is large and the
        name is small but always there - see `Classification.tsx` on colour
        never being the only carrier of meaning; the same goes for a picture.
        A category without an emoji shows its colour dot in the same place, so
        the grid stays regular.
      */}
      <div role="list" className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="quick-add-categories">
        {tiles.map((tile) => (
          <div role="listitem" key={tile.id}>
            <button
              type="button"
              onClick={() => onPick(tile)}
              className={cn(
                'flex min-h-24 w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-input bg-card p-2 text-center transition-colors',
                'hover:border-primary hover:bg-accent active:bg-accent',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              <CategoryMark emoji={tile.emoji} colour={tile.colour} className="text-4xl" />
              <span className="line-clamp-2 text-xs leading-tight">{tile.name}</span>
            </button>
          </div>
        ))}
        {allowNone ? (
          <div role="listitem">
            <button
              type="button"
              onClick={() => onPick(null)}
              className={cn(
                'flex min-h-24 w-full flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-input bg-card p-2 text-center text-muted-foreground transition-colors',
                'hover:border-primary hover:bg-accent active:bg-accent',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              <span aria-hidden="true" className="text-4xl leading-none">
                🤷
              </span>
              <span className="text-xs leading-tight">Not sure</span>
            </button>
          </div>
        ) : null}
      </div>
      {tiles.length === 0 && !allowNone ? (
        <p className="text-sm text-muted-foreground">
          No categories yet.{' '}
          <Link to="/settings/categories" className="underline" data-tap-exempt="inline text link in a sentence">
            Add one in Settings
          </Link>
          .
        </p>
      ) : null}
    </div>
  );
}

// ── Step 3: how much, when, from where ────────────────────────────────────

function AmountStep({
  kind,
  currency,
  amount,
  onAmount,
  valid,
  date,
  today,
  onDate,
  accounts,
  accountsLoading,
  accountsError,
  accountId,
  onAccount,
  note,
  onNote,
  onNext,
}: {
  kind: QuickAddKind;
  currency: string;
  amount: string;
  onAmount: (value: string) => void;
  valid: boolean;
  date: string;
  today: string;
  onDate: (value: string) => void;
  accounts: Account[];
  accountsLoading: boolean;
  accountsError?: unknown;
  accountId: string;
  onAccount: (id: string) => void;
  note: string;
  onNote: (value: string) => void;
  onNext: () => void;
}) {
  const amountRef = useRef<HTMLInputElement>(null);
  const [pickingDate, setPickingDate] = useState(false);
  const [changingAccount, setChangingAccount] = useState(false);
  const yesterday = dayBefore(today);
  const account = accounts.find((a) => a.id === accountId);
  // The screen opens with the keypad up. It is the one field that matters,
  // and a person on a phone should not have to find it.
  useEffect(() => amountRef.current?.focus(), []);

  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid && account) onNext();
      }}
    >
      <div className="space-y-5">
        <div className="space-y-1.5">
          <label htmlFor="quick-add-amount" className="text-base font-medium">
            How much?
          </label>
          {/* The BOX shows focus, not the field inside it - one ring around
              the whole control rather than a ring inside a border. The size
              is on the box too: the field inherits it, which is how it keeps
              36px text under the touch rule that floors every field at 16px. */}
          <div className="flex min-h-16 items-center gap-2 rounded-xl border-2 border-input bg-background px-4 text-4xl focus-within:border-primary focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
            <span aria-hidden="true" className="text-3xl font-semibold text-muted-foreground">
              {currencyMeta(currency).symbol}
            </span>
            <input
              ref={amountRef}
              id="quick-add-amount"
              // `decimal` brings up the number pad with a point on it. NOT
              // type="number": that adds spinners, refuses a comma, and on some
              // phones swallows the value when a person types "1,250".
              inputMode="decimal"
              autoComplete="off"
              value={amount}
              onChange={(event) => onAmount(event.target.value)}
              placeholder="0"
              aria-describedby="quick-add-amount-hint"
              className="w-full bg-transparent font-semibold tabular-nums outline-none placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <p id="quick-add-amount-hint" className="text-xs text-muted-foreground">
            {kind === 'spent' ? 'What the receipt says, tax included.' : 'What actually arrived.'}
          </p>
        </div>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">When?</legend>
          <div className="flex flex-wrap gap-2">
            <Chip selected={date === today && !pickingDate} onClick={() => { onDate(today); setPickingDate(false); }}>
              Today
            </Chip>
            <Chip selected={date === yesterday && !pickingDate} onClick={() => { onDate(yesterday); setPickingDate(false); }}>
              Yesterday
            </Chip>
            <Chip selected={pickingDate || (date !== today && date !== yesterday)} onClick={() => setPickingDate(true)}>
              {date !== today && date !== yesterday ? formatDateLong(date) : 'Pick a day'}
            </Chip>
          </div>
          {pickingDate ? (
            <Input
              type="date"
              aria-label="Date"
              value={date}
              max={today}
              onChange={(event) => onDate(event.target.value || today)}
              className="max-w-xs"
            />
          ) : null}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className="text-sm font-medium">{kind === 'spent' ? 'Paid from' : 'Into'}</legend>
          {accountsError ? (
            <p className="text-sm text-destructive" role="alert">
              {accountsError instanceof Error ? accountsError.message : 'The accounts did not load.'}
            </p>
          ) : accountsLoading ? (
            <Spinner />
          ) : accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No account to pay from yet.{' '}
              <Link to="/banking" className="underline" data-tap-exempt="inline text link in a sentence">
                Add one in Banking
              </Link>
              .
            </p>
          ) : changingAccount || !account ? (
            <div className="flex flex-wrap gap-2">
              {accounts.map((option) => (
                <Chip
                  key={option.id}
                  selected={option.id === accountId}
                  onClick={() => {
                    onAccount(option.id);
                    setChangingAccount(false);
                  }}
                >
                  {option.name}
                </Chip>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Chip selected onClick={() => setChangingAccount(true)}>
                {account.name}
              </Chip>
              {accounts.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setChangingAccount(true)}
                  className="min-h-11 px-2 text-sm text-primary underline-offset-4 hover:underline"
                >
                  Change
                </button>
              ) : null}
            </div>
          )}
        </fieldset>

        <Input
          label="What was it?"
          value={note}
          onChange={(event) => onNote(event.target.value)}
          maxLength={300}
          placeholder={kind === 'spent' ? 'Lunch with the team' : 'Cash from a customer'}
          hint="Optional. A few words you will recognise later."
        />
      </div>

      <StepFooter>
        <Button type="submit" size="lg" className="w-full text-base" disabled={!valid || !account}>
          Next
        </Button>
      </StepFooter>
    </form>
  );
}

/**
 * The step's primary button, pinned to the bottom of the screen.
 *
 * Sticky within the shell's scroller, so it stays put while the fields above
 * it scroll under the keyboard; padded above the home indicator; on its own
 * background so the fields scroll behind it rather than through it. The
 * negative horizontal margin runs the background to the page edge.
 */
function StepFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky bottom-0 -mx-4 mt-auto bg-background px-4 pb-[max(1rem,var(--safe-bottom))] pt-3 sm:-mx-5 sm:px-5">
      {children}
    </div>
  );
}

/** A pill the size of a thumb, with a check when it is the one chosen. */
function Chip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        selected
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-input bg-background text-foreground hover:bg-accent active:bg-accent',
      )}
    >
      {selected ? <Check className="size-4" aria-hidden /> : null}
      {children}
    </button>
  );
}

// ── Step 4: is that right ─────────────────────────────────────────────────

function ConfirmStep({
  sentence,
  note,
  pending,
  error,
  onSave,
  onChange,
}: {
  sentence: string;
  note: string;
  pending: boolean;
  error?: string;
  onSave: () => void;
  onChange: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col">
      <div className="space-y-5">
        <p className="text-base font-medium">Is this right?</p>
        <div className="rounded-xl border border-border bg-card p-5">
          <p className="text-xl leading-snug" data-testid="quick-add-sentence">
            {sentence}
          </p>
          {note ? <p className="mt-2 text-sm text-muted-foreground">“{note}”</p> : null}
        </div>
        {error ? (
          <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <StepFooter>
        <div className="space-y-2">
          <Button size="lg" className="w-full text-base" loading={pending} onClick={onSave} autoFocus>
            Save
          </Button>
          <Button variant="ghost" size="lg" className="w-full" onClick={onChange} disabled={pending}>
            Change something
          </Button>
        </div>
      </StepFooter>
    </div>
  );
}

function SavedStep({
  sentence,
  onAnother,
  onDone,
}: {
  sentence: string;
  onAnother: () => void;
  onDone: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-10 pb-[max(2.5rem,var(--safe-bottom))] text-center" role="status">
      <span className="flex size-16 items-center justify-center rounded-full bg-success/15 text-success">
        <Check className="size-9" aria-hidden />
      </span>
      <p className="text-2xl font-semibold">Saved ✓</p>
      <p className="max-w-sm text-sm text-muted-foreground">{sentence.replace(/\.$/, '')}.</p>
      <div className="mt-4 flex w-full max-w-xs flex-col gap-2">
        <Button size="lg" className="w-full text-base" onClick={onAnother} autoFocus>
          Add another
        </Button>
        <Button variant="outline" size="lg" className="w-full" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
